'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useState } from 'react';

import cx from 'clsx';
import { useFavorites } from 'contexts/FavoritesContext';

import { useRealtimeFavoriteCount } from '../../../hooks/useRealtimeFavoriteCount';
import {
    FavoriteButtonPropTypes,
    favoritesUtils,
} from '../../../utils/favorites';

import styles from './FavoriteButton.module.scss';

const FavoriteButton = ({
    itemId,
    itemType,
    itemTitle,
    itemSlug,
    itemImage,
    size = 'medium',
    variant = 'default',
    showCount = false,
    realtime = false,
    disabled = false,
    className,
    onClick,
}) => {
    const {
        isFavorited,
        addFavorite,
        removeFavorite,
        totalCounts,
        getFavoriteCounts,
        initialized,
    } = useFavorites();

    const countKey = `${itemType}:${itemId}`;

    const [isLoading, setIsLoading] = useState(false);
    const [count, setCount] = useState(totalCounts[countKey] || 0); // Immediate UI feedback
    const [previousCount, setPreviousCount] = useState(null); // Track previous count for animations
    const [animatingHearts, setAnimatingHearts] = useState([]); // Track animated hearts
    const [countLoaded, setCountLoaded] = useState(false); // Track if count has been loaded
    const [isClient, setIsClient] = useState(false); // Track if we're on the client
    const favorited = isFavorited(itemId, itemType);

    // Handle client-side hydration
    React.useEffect(() => {
        setIsClient(true);
    }, []);

    // Use realtime count if realtime prop is enabled, otherwise use context count
    const realtimeCount = useRealtimeFavoriteCount(
        itemType,
        itemId,
        realtime && showCount
    );

    // Trigger heart animation when count increases
    React.useEffect(() => {
        if (showCount && previousCount !== null && count > previousCount) {
            const heartId = Date.now() + Math.random();
            const newHeart = {
                id: heartId,
                x: Math.random() * 20 - 10, // Random x offset (-10 to 10px)
                y: 0,
                rotation: Math.random() * 60 - 30, // Random rotation (-30 to 30 degrees)
                scale: 0.8 + Math.random() * 0.4, // Random scale (0.8 to 1.2)
            };

            setAnimatingHearts(prev => [...prev, newHeart]);

            // Remove heart after animation completes
            setTimeout(() => {
                setAnimatingHearts(prev =>
                    prev.filter(heart => heart.id !== heartId)
                );
            }, 1000); // Match animation duration
        }

        setPreviousCount(count);
    }, [count, previousCount, showCount]);

    // Load count on mount if not already loaded and not using realtime
    React.useEffect(() => {
        if (showCount && !realtime && !(countKey in totalCounts)) {
            getFavoriteCounts([countKey]).then(() => {
                setCountLoaded(true);
            });
        } else if (showCount && countKey in totalCounts) {
            setCountLoaded(true);
        } else if (!showCount) {
            setCountLoaded(true); // No count needed, so consider it "loaded"
        }
    }, [countKey, showCount, realtime, totalCounts, getFavoriteCounts]);

    // Load count on mount if not already loaded and not using realtime
    React.useEffect(() => {
        setCount(realtimeCount);
    }, [realtimeCount]);

    // Load count on mount if not already loaded and not using realtime
    React.useEffect(() => {
        if (showCount) {
            getFavoriteCounts([countKey]).then(() => {
                setCountLoaded(true);
            });
        }
    }, [countKey, getFavoriteCounts, showCount]);

    const handleClick = useCallback(
        async event => {
            event.preventDefault();
            event.stopPropagation();

            if (disabled || isLoading) return;

            console.log(`🔘 FavoriteButton clicked for ${itemType}:${itemId}`, {
                favorited,
                currentCount: count,
                realtime,
                showCount,
            });

            setIsLoading(true);

            // Set optimistic count for immediate feedback
            if (realtime && showCount) {
                const currentDisplayedCount = count;

                // Set optimistic count based on action
                if (favorited) {
                    setCount(Math.max(0, currentDisplayedCount - 1));
                } else {
                    setCount(currentDisplayedCount + 1);
                }
            }

            try {
                if (favorited) {
                    console.log(
                        `➖ Removing favorite for ${itemType}:${itemId}`
                    );
                    await removeFavorite(itemId, itemType);
                } else {
                    console.log(`➕ Adding favorite for ${itemType}:${itemId}`);
                    await addFavorite({
                        id: itemId,
                        type: itemType,
                        title: itemTitle,
                        slug: itemSlug,
                        image: itemImage,
                    });
                }

                console.log(
                    `✅ Favorite operation completed for ${itemType}:${itemId}`
                );

                // Realtime subscription will handle count updates automatically

                // Call custom onClick if provided
                if (onClick) {
                    onClick(event, !favorited);
                }
            } catch (error) {
                console.error('Error toggling favorite:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [
            disabled,
            isLoading,
            favorited,
            itemId,
            itemType,
            itemTitle,
            itemSlug,
            itemImage,
            addFavorite,
            removeFavorite,
            onClick,
            count,
            realtime,
            showCount,
        ]
    );

    // Only show as initialized on the client side to prevent hydration mismatch
    const shouldShowAsInitialized =
        isClient && (showCount ? initialized && countLoaded : initialized);

    const buttonClasses = cx(
        styles.favoriteButton,
        styles[`size-${size}`],
        styles[`variant-${variant}`],
        {
            [styles.favorited]: favorited,
            // [styles.loading]: isLoading,
            [styles.disabled]: disabled,
            [styles.initialized]: shouldShowAsInitialized,
        },
        className
    );

    return (
        <div className={styles.favoriteButtonContainer}>
            <button
                type="button"
                className={buttonClasses}
                onClickCapture={handleClick}
                disabled={disabled || isLoading}
                aria-label={
                    favorited
                        ? `Remove ${itemTitle} from favorites`
                        : `Add ${itemTitle} to favorites`
                }
                aria-pressed={favorited}
                title={favorited ? `Remove from favorites` : `Add to favorites`}
            >
                <span className={styles.icon} aria-hidden="true">
                    <svg
                        viewBox="0 0 30 31"
                        className={styles.heart}
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M15 10.5C15 10.5 15 10.5 14.05 9.25C12.95 7.8 11.325 6.75 9.375 6.75C6.2625 6.75 3.75 9.2625 3.75 12.375C3.75 13.5375 4.1 14.6125 4.7 15.5C5.7125 17.0125 15 26.75 15 26.75M15 10.5C15 10.5 15 10.5 15.95 9.25C17.05 7.8 18.675 6.75 20.625 6.75C23.7375 6.75 26.25 9.2625 26.25 12.375C26.25 13.5375 25.9 14.6125 25.3 15.5C24.2875 17.0125 15 26.75 15 26.75"
                            fill={'currentColor'}
                        />
                        <path
                            d="M15 10.5C15 10.5 15 10.5 14.05 9.25C12.95 7.8 11.325 6.75 9.375 6.75C6.2625 6.75 3.75 9.2625 3.75 12.375C3.75 13.5375 4.1 14.6125 4.7 15.5C5.7125 17.0125 15 26.75 15 26.75C15 26.75 24.2875 17.0125 25.3 15.5C25.9 14.6125 26.25 13.5375 26.25 12.375C26.25 9.2625 23.7375 6.75 20.625 6.75C18.675 6.75 17.05 7.8 15.95 9.25C15 10.5 15 10.5 15 10.5Z"
                            stroke="#001E60"
                            strokeWidth="1.875"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                {showCount && count > 0 && (
                    <span
                        className={styles.count}
                        aria-label={`${count} favorites`}
                    >
                        {count === 0 ? null : favoritesUtils.formatCount(count)}
                    </span>
                )}
            </button>

            {/* Animated hearts overlay */}
            {animatingHearts.map(heart => (
                <div
                    key={heart.id}
                    className={styles.animatedHeart}
                    style={{
                        '--heart-x': `${heart.x}px`,
                        '--heart-y': `${heart.y}px`,
                        '--heart-rotation': `${heart.rotation}deg`,
                        '--heart-scale': heart.scale,
                    }}
                >
                    <svg viewBox="0 0 24 24" className={styles.heartIcon}>
                        <path
                            d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                            fill="currentColor"
                            stroke="none"
                        />
                    </svg>
                </div>
            ))}
        </div>
    );
};

FavoriteButton.propTypes = FavoriteButtonPropTypes;

export default React.memo(FavoriteButton);
